import React, { useState, useEffect } from "react";
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useAuth } from "@/contexts/AuthContext";
import EditProfileForm from "./EditProfileForm";
import ProfileQuote from "./ProfileQuote";
import { supabase } from "@/integrations/supabase/client";
import ProfileStats from "./ProfileStats";
import ProfileActions from "./ProfileActions";
import ProfileSocialLinks from "./ProfileSocialLinks";
import AuraBadge from "@/components/common/AuraBadge";
import { cn } from "@/lib/utils";
import ProfileWalletInfo from "./ProfileWalletInfo";
import ProfileNameDisplay from "./ProfileNameDisplay";
import ProfileBio from "./ProfileBio";
import { ErrorBoundary } from "@/components/shared/ErrorBoundary";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface ProfileInfoProps {
  profile: any;
}

const ProfileInfo: React.FC<ProfileInfoProps> = ({ profile: initialProfile }) => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [totalLikes, setTotalLikes] = useState(0);
  const [imageError, setImageError] = useState(false);
  const [imageKey, setImageKey] = useState(Date.now());
  const [currentProfile, setCurrentProfile] = useState(initialProfile);
  const isOwnProfile = user?.id === initialProfile?.id;

  console.log("ProfileInfo - Props:", {
    initialProfile,
    user,
    isOwnProfile,
    isEditing
  });

  useEffect(() => {
    if (!initialProfile?.id) return;

    let channel = supabase
      .channel(`profile-changes-${initialProfile.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${initialProfile.id}`,
        },
        (payload) => {
          console.log('Profile updated:', payload);
          setCurrentProfile(payload.new);
          setImageKey(Date.now());
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [initialProfile?.id]);

  useEffect(() => {
    const fetchTotalLikes = async () => {
      if (!currentProfile?.id) return;
      
      const { data: bulletins, error } = await supabase
        .from('bulletins')
        .select('id, like_count')
        .eq('user_id', currentProfile.id);
      
      if (error) {
        console.error('Error fetching bulletins for like count:', error);
        return;
      }
      
      const total = bulletins?.reduce((sum, bulletin) => sum + (bulletin.like_count || 0), 0) || 0;
      setTotalLikes(total);
      
      const channel = supabase
        .channel(`bulletins-likes-${currentProfile.id}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'bulletins',
            filter: `user_id=eq.${currentProfile.id}`,
          },
          async (payload) => {
            console.log('Bulletin updated, recalculating likes:', payload);
            
            const { data: updatedBulletins } = await supabase
              .from('bulletins')
              .select('like_count')
              .eq('user_id', currentProfile.id);
            
            const newTotal = updatedBulletins?.reduce(
              (sum, bulletin) => sum + (bulletin.like_count || 0), 
              0
            ) || 0;
            
            setTotalLikes(newTotal);
          }
        )
        .subscribe();
      
      return () => {
        supabase.removeChannel(channel);
      };
    };

    fetchTotalLikes();
  }, [currentProfile?.id]);

  const getCleanAvatarUrl = () => {
    if (!currentProfile?.profile_image_url) return null;
    const baseUrl = currentProfile.profile_image_url.split('?')[0];
    return `${baseUrl}?t=${imageKey}`;
  };

  if (!currentProfile) {
    return (
      <div className="space-y-6 text-center">
        <p className="text-gray-400">Profile not found</p>
      </div>
    );
  }

  const avatarUrl = getCleanAvatarUrl();

  return (
    <>
      {/* Edit Profile Modal */}
      <Dialog open={isEditing && isOwnProfile} onOpenChange={(open) => !open && setIsEditing(false)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#1A1F2C] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Edit Profile</DialogTitle>
          </DialogHeader>
          <EditProfileForm 
            profile={currentProfile} 
            onCancel={() => setIsEditing(false)} 
          />
        </DialogContent>
      </Dialog>

      <div className="space-y-6">
      <div className="mb-2 relative flex justify-center md:justify-start md:inline-block -mt-20">
        <div className={cn(
          "rounded-full p-[4px]",
          currentProfile.is_admin && "bg-gradient-to-t from-[#F97316] to-[#FEF7CD]",
          currentProfile.is_premium && "bg-gradient-to-r from-[#1A1F2C] via-[#7E69AB] to-[#FFD700]",
        )}>
          <Avatar className={cn(
            "w-[175px] h-[175px] md:w-[250px] md:h-[250px]",
            !currentProfile.is_admin && !currentProfile.is_premium && "ring-1 ring-primary/20",
            "bg-[#1A1F2C]"
          )}>
            {!imageError && avatarUrl && (
              <AvatarImage 
                src={avatarUrl}
                alt={currentProfile.display_name || currentProfile.username}
                onError={() => {
                  console.error("Error loading avatar image in profile");
                  setImageError(true);
                }}
                className="object-cover"
              />
            )}
            <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/40 text-white text-4xl font-bold">
              {currentProfile.profile_image_url ? '' : 
                (
                  (currentProfile.display_name || currentProfile.username || "U")
                    .charAt(0)
                    .toUpperCase()
                )
              }
            </AvatarFallback>
          </Avatar>
        </div>
        <div className="absolute bottom-6 -right-1">
          <AuraBadge value={currentProfile.elo_rating} size="md" />
        </div>
      </div>

      <ProfileWalletInfo 
        walletAddress={currentProfile.wallet_address}
        registrationNumber={currentProfile.registration_number}
      />

      <ProfileQuote profile={currentProfile} isOwnProfile={isOwnProfile} />

      <ProfileNameDisplay 
        displayName={currentProfile.display_name}
        username={currentProfile.username}
        isAdmin={currentProfile.is_admin}
        isPartner={currentProfile.is_partner}
        isPremium={currentProfile.is_premium}
        isSpecialUsername={currentProfile.is_special_username}
        tagCode={currentProfile.tag_code}
        tagEmote={currentProfile.tag_emote}
      />

      <ProfileBio bio={currentProfile.bio} />

      <ProfileStats 
        followersCount={currentProfile.followers_count || 0}
        followingCount={currentProfile.following_count || 0}
        totalLikes={totalLikes}
        eloRating={currentProfile.elo_rating}
        profileId={currentProfile.id}
      />

      <ProfileActions 
        isOwnProfile={isOwnProfile}
        onEdit={() => setIsEditing(true)}
        profileId={currentProfile.id}
        profile={currentProfile}
      />

      <div className="mt-6">
        <ErrorBoundary
          fallback={
            <div className="text-center text-muted-foreground py-4">
              <p>Unable to load social links</p>
            </div>
          }
        >
          <ProfileSocialLinks profile={currentProfile} />
        </ErrorBoundary>
      </div>
    </div>
    </>
  );
};

export default ProfileInfo;
